import React, { useState, useCallback } from 'react';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import {
  Plus, Trash2, Copy, ChevronUp, ChevronDown,
  Type, Image, Minus, AlignLeft, Layout as LayoutIcon,
  Eye, Code, Palette, Download
} from 'lucide-react';

// ─── Block Types ─────────────────────────────────────────────────────────────
const BLOCK_TYPES = [
  { type: 'header',    label: 'Header',      icon: Type },
  { type: 'text',      label: 'Text',        icon: AlignLeft },
  { type: 'image',     label: 'Image',       icon: Image },
  { type: 'button',    label: 'Button',      icon: LayoutIcon },
  { type: 'divider',   label: 'Divider',     icon: Minus },
  { type: 'spacer',    label: 'Spacer',      icon: Minus },
];

const defaultBlock = (type) => {
  const base = { id: `${type}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, type };
  switch (type) {
    case 'header':  return { ...base, text: 'Your Headline Here', size: '28', color: '#ffffff', bg: '#111111', align: 'center', bold: true };
    case 'text':    return { ...base, text: 'Write your message here. Keep it short and to the point — residents read this on their phones.', color: '#cccccc', bg: '#0d0d0d', size: '15', align: 'left' };
    case 'image':   return { ...base, url: 'https://cyqr.ca/logo.png', alt: 'CYQR', width: '100%', link: '' };
    case 'button':  return { ...base, text: 'Read More on CYQR.CA', url: 'https://cyqr.ca', bg: '#C5A52A', color: '#000000', align: 'center' };
    case 'divider': return { ...base, color: '#2a2a2a', thickness: '1' };
    case 'spacer':  return { ...base, height: '24' };
    default:        return base;
  }
};

// ─── Block Renderer (inline preview) ─────────────────────────────────────────
const BlockPreview = ({ block }) => {
  const s = { fontFamily: 'Arial, sans-serif' };
  switch (block.type) {
    case 'header':  return <div style={{ ...s, background: block.bg, color: block.color, fontSize: `${block.size}px`, fontWeight: block.bold ? 'bold' : 'normal', textAlign: block.align, padding: '20px 24px' }}>{block.text}</div>;
    case 'text':    return <div style={{ ...s, background: block.bg, color: block.color, fontSize: `${block.size}px`, textAlign: block.align, padding: '12px 24px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{block.text}</div>;
    case 'image':   return <div style={{ background: '#0d0d0d', textAlign: block.align || 'center', padding: '8px 24px' }}><img src={block.url} alt={block.alt} style={{ maxWidth: block.width || '100%', height: 'auto' }} onError={e => { e.target.style.opacity=0.3; }} /></div>;
    case 'button':  return <div style={{ background: '#0d0d0d', textAlign: block.align || 'center', padding: '16px 24px' }}><a href={block.url} style={{ display: 'inline-block', background: block.bg, color: block.color, padding: '12px 28px', textDecoration: 'none', fontWeight: 'bold', fontSize: '14px', fontFamily: 'Arial, sans-serif' }}>{block.text}</a></div>;
    case 'divider': return <div style={{ background: '#0d0d0d', padding: '4px 24px' }}><div style={{ height: `${block.thickness || 1}px`, background: block.color }} /></div>;
    case 'spacer':  return <div style={{ height: `${block.height}px`, background: '#0d0d0d' }} />;
    default:        return null;
  }
};

// ─── Block HTML Generator ─────────────────────────────────────────────────────
const blockToHtml = (block) => {
  switch (block.type) {
    case 'header':  return `<tr><td style="background:${block.bg};padding:20px 24px;text-align:${block.align};font-family:Arial,sans-serif;font-size:${block.size}px;font-weight:${block.bold?'bold':'normal'};color:${block.color};">${block.text}</td></tr>`;
    case 'text':    return `<tr><td style="background:${block.bg};padding:12px 24px;text-align:${block.align};font-family:Arial,sans-serif;font-size:${block.size}px;color:${block.color};line-height:1.6;">${block.text.replace(/\n/g,'<br>')}</td></tr>`;
    case 'image':   return `<tr><td style="background:#0d0d0d;padding:8px 24px;text-align:center;">${block.link?`<a href="${block.link}">`:''}<img src="${block.url}" alt="${block.alt}" width="${block.width==='100%'?'100%':block.width}" style="max-width:100%;height:auto;display:block;margin:0 auto;">${block.link?'</a>':''}</td></tr>`;
    case 'button':  return `<tr><td style="background:#0d0d0d;padding:16px 24px;text-align:${block.align};"><a href="${block.url}" style="display:inline-block;background:${block.bg};color:${block.color};padding:12px 28px;text-decoration:none;font-weight:bold;font-size:14px;font-family:Arial,sans-serif;">${block.text}</a></td></tr>`;
    case 'divider': return `<tr><td style="background:#0d0d0d;padding:4px 24px;"><div style="height:${block.thickness||1}px;background:${block.color};"></div></td></tr>`;
    case 'spacer':  return `<tr><td style="background:#0d0d0d;height:${block.height}px;"></td></tr>`;
    default:        return '';
  }
};

const generateHtml = (blocks, subject, previewText, brandColor) => `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<title>${subject}</title>
<meta name="description" content="${previewText}">
<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
</head>
<body style="margin:0;padding:0;background:#1a1a1a;font-family:Arial,sans-serif;">
<!-- Preview text (hidden) -->
<div style="display:none;font-size:1px;color:#1a1a1a;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;">${previewText}</div>
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#1a1a1a;">
<tr><td align="center" style="padding:20px 0;">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#0d0d0d;">

<!-- Header bar -->
<tr><td style="background:${brandColor};padding:12px 24px;text-align:center;">
<span style="color:#000;font-weight:bold;font-family:Arial,sans-serif;font-size:13px;letter-spacing:2px;text-transform:uppercase;">CYQR.CA — Show Them Your Regina</span>
</td></tr>

${blocks.map(blockToHtml).join('\n')}

<!-- Footer -->
<tr><td style="background:#111;padding:20px 24px;text-align:center;border-top:1px solid #2a2a2a;">
<p style="color:#555;font-size:12px;font-family:Arial,sans-serif;margin:0 0 8px;">You're receiving this because you opted in at <a href="https://cyqr.ca" style="color:${brandColor};text-decoration:none;">cyqr.ca</a></p>
<p style="color:#444;font-size:11px;font-family:Arial,sans-serif;margin:0;">Regina, SK · <a href="https://cyqr.ca/unsubscribe" style="color:#555;text-decoration:underline;">Unsubscribe</a></p>
</td></tr>

</table>
</td></tr>
</table>
</body>
</html>`;

// ─── Block Editor Panel ───────────────────────────────────────────────────────
const BlockEditor = ({ block, onChange }) => {
  const field = (key, label, type='text', opts={}) => (
    <div key={key}>
      <label className="block font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea value={block[key]||''} onChange={e=>onChange(key,e.target.value)} rows={4}
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-xs p-2 font-mono resize-y focus:border-[#C5A52A] outline-none" />
      ) : type === 'select' ? (
        <select value={block[key]||''} onChange={e=>onChange(key,e.target.value)}
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-xs p-2 font-mono focus:border-[#C5A52A] outline-none">
          {opts.options?.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === 'color' ? (
        <div className="flex items-center gap-2">
          <input type="color" value={block[key]||'#ffffff'} onChange={e=>onChange(key,e.target.value)}
            className="w-8 h-8 cursor-pointer border-0 bg-transparent p-0" />
          <span className="font-mono text-xs text-gray-400">{block[key]}</span>
        </div>
      ) : type === 'checkbox' ? (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!block[key]} onChange={e=>onChange(key,e.target.checked)}
            className="w-4 h-4 accent-[#C5A52A]" />
          <span className="text-xs text-gray-400">{opts.checkLabel}</span>
        </label>
      ) : (
        <input type={type} value={block[key]||''} onChange={e=>onChange(key,e.target.value)}
          className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-xs p-2 font-mono focus:border-[#C5A52A] outline-none" />
      )};
    </div>
  );

  const alignOpts = { options: [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }] };

  return (
    <div className="space-y-3 p-4">
      {block.type === 'header' && <>
        {field('text',  'Headline Text', 'textarea')}
        {field('size',  'Font Size (px)', 'number')}
        {field('color', 'Text Color', 'color')}
        {field('bg',    'Background Color', 'color')}
        {field('align', 'Alignment', 'select', alignOpts)}
        {field('bold',  'Bold', 'checkbox', { checkLabel: 'Bold text' })}
      </>}
      {block.type === 'text' && <>
        {field('text',  'Body Text', 'textarea')}
        {field('size',  'Font Size (px)', 'number')}
        {field('color', 'Text Color', 'color')}
        {field('bg',    'Background Color', 'color')}
        {field('align', 'Alignment', 'select', alignOpts)}
      </>}
      {block.type === 'image' && <>
        {field('url',   'Image URL', 'text')}
        {field('alt',   'Alt Text', 'text')}
        {field('link',  'Click-through URL (optional)', 'text')}
        {field('width', 'Width (px or %)', 'text')}
      </>}
      {block.type === 'button' && <>
        {field('text',  'Button Label', 'text')}
        {field('url',   'Link URL', 'text')}
        {field('bg',    'Button Color', 'color')}
        {field('color', 'Text Color', 'color')}
        {field('align', 'Alignment', 'select', alignOpts)}
      </>}
      {block.type === 'divider' && <>
        {field('color',     'Line Color', 'color')}
        {field('thickness', 'Thickness (px)', 'number')}
      </>}
      {block.type === 'spacer' && <>
        {field('height', 'Height (px)', 'number')}
      </>}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const EmailDesigner = () => {
  const { isAdmin } = useAuth();

  const [blocks, setBlocks] = useState([
    defaultBlock('header'),
    defaultBlock('text'),
    defaultBlock('button'),
  ]);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState('Update from CYQR.CA');
  const [previewText, setPreviewText] = useState('Here\'s what\'s happening in your ward this week.');
  const [brandColor, setBrandColor] = useState('#C5A52A');
  const [mode, setMode] = useState('design'); // 'design' | 'preview' | 'code'
  const [copied, setCopied] = useState(false);

  const updateBlock = useCallback((id, key, value) => {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, [key]: value } : b));
  }, []);

  if (!isAdmin()) return <Navigate to="/" replace />;

  const addBlock = (type) => {
    const nb = defaultBlock(type);
    setBlocks(b => [...b, nb]);
    setSelected(nb.id);
  };

  const removeBlock = (id) => {
    setBlocks(b => b.filter(bl => bl.id !== id));
    if (selected === id) setSelected(null);
  };

  const moveBlock = (id, dir) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b.id === id);
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const html = generateHtml(blocks, subject, previewText, brandColor);

  const copyHtml = () => {
    navigator.clipboard.writeText(html).then(() => {
      setCopied(true);
      toast.success('HTML copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const downloadHtml = () => {
    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cyqr-email-${subject.replace(/\s+/g,'-').toLowerCase()}.html`;
    a.click();
  };

  const selectedBlock = blocks.find(b => b.id === selected);

  return (
    <Layout>
      <div className="min-h-screen bg-[#0D0D0D] text-white">

        {/* Top bar */}
        <div className="bg-[#111] border-b border-[#1E1E1E] sticky top-0 z-40">
          <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-[#C5A52A] rounded-full" />
              <h1 className="font-display text-sm text-white tracking-wider">EMAIL TEMPLATE DESIGNER</h1>
              <span className="font-mono text-[10px] text-gray-600 bg-[#0d0d0d] border border-[#2a2a2a] px-2 py-0.5">UI ONLY — no emails are sent</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Mode tabs */}
              {[['design','Design',LayoutIcon], ['preview','Preview',Eye], ['code','HTML Code',Code]].map(([m,label,Icon]) => (
                <button key={m} onClick={() => setMode(m)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs transition-colors ${mode===m ? 'bg-[#C5A52A] text-black' : 'text-gray-400 hover:text-white border border-[#2a2a2a]'}`}>
                  <Icon className="w-3 h-3" />{label}
                </button>
              ))}
              <div className="w-px h-5 bg-[#2a2a2a]" />
              <button onClick={copyHtml}
                className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-[#2a2a2a] text-gray-300 hover:text-white'}`}>
                <Copy className="w-3 h-3" />{copied ? 'Copied!' : 'Copy HTML'}
              </button>
              <button onClick={downloadHtml}
                className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs bg-[#2a2a2a] text-gray-300 hover:text-white transition-colors">
                <Download className="w-3 h-3" />Download
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-4 py-6">

          {/* Email metadata */}
          <div className="bg-[#111] border border-[#1E1E1E] p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-1">Subject Line</label>
              <input value={subject} onChange={e=>setSubject(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-sm p-2 font-mono focus:border-[#C5A52A] outline-none" />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-1">Preview Text (inbox snippet)</label>
              <input value={previewText} onChange={e=>setPreviewText(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-white text-sm p-2 font-mono focus:border-[#C5A52A] outline-none" />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-1">Brand Accent Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={brandColor} onChange={e=>setBrandColor(e.target.value)}
                  className="w-10 h-9 cursor-pointer border border-[#2a2a2a] bg-transparent p-0.5" />
                <span className="font-mono text-xs text-gray-400">{brandColor}</span>
              </div>
            </div>
          </div>

          {/* DESIGN mode */}
          {mode === 'design' && (
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_280px] gap-4">

              {/* LEFT: Add blocks */}
              <div className="bg-[#111] border border-[#1E1E1E] p-4">
                <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wider mb-3">Add Block</p>
                <div className="space-y-1">
                  {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
                    <button key={type} onClick={() => addBlock(type)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-gray-400 hover:text-white hover:bg-[#1a1a1a] transition-colors font-mono">
                      <Plus className="w-3 h-3 text-[#C5A52A] flex-shrink-0" />
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* CENTRE: Block list + live preview */}
              <div className="space-y-2">
                {blocks.length === 0 && (
                  <div className="border-2 border-dashed border-[#2a2a2a] p-16 text-center">
                    <p className="text-gray-600 font-mono text-sm">Add a block from the left panel</p>
                  </div>
                )}
                {blocks.map((block, idx) => (
                  <div key={block.id}
                    onClick={() => setSelected(block.id)}
                    className={`border-2 cursor-pointer transition-all ${selected===block.id ? 'border-[#C5A52A]' : 'border-transparent hover:border-[#2a2a2a]'}`}>
                    {/* Block controls */}
                    <div className={`flex items-center justify-between px-2 py-1 ${selected===block.id ? 'bg-[#C5A52A]/10' : 'bg-[#111]'}`}>
                      <span className="font-mono text-[10px] text-gray-600 uppercase">{block.type}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={e=>{e.stopPropagation();moveBlock(block.id,-1);}} disabled={idx===0}
                          className="p-0.5 text-gray-600 hover:text-white disabled:opacity-30"><ChevronUp className="w-3 h-3"/></button>
                        <button onClick={e=>{e.stopPropagation();moveBlock(block.id,1);}} disabled={idx===blocks.length-1}
                          className="p-0.5 text-gray-600 hover:text-white disabled:opacity-30"><ChevronDown className="w-3 h-3"/></button>
                        <button onClick={e=>{e.stopPropagation();removeBlock(block.id);}}
                          className="p-0.5 text-red-500 hover:text-red-400"><Trash2 className="w-3 h-3"/></button>
                      </div>
                    </div>
                    <BlockPreview block={block} />
                  </div>
                ))}
              </div>

              {/* RIGHT: Editor panel */}
              <div className="bg-[#111] border border-[#1E1E1E]">
                {selectedBlock ? (
                  <>
                    <div className="px-4 py-3 border-b border-[#1E1E1E] flex items-center gap-2">
                      <Palette className="w-4 h-4 text-[#C5A52A]" />
                      <span className="font-mono text-xs text-white uppercase tracking-wider">Edit {selectedBlock.type}</span>
                    </div>
                    <BlockEditor
                      block={selectedBlock}
                      onChange={(key, value) => updateBlock(selectedBlock.id, key, value)}
                    />
                  </>
                ) : (
                  <div className="p-6 text-center">
                    <Palette className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                    <p className="text-gray-600 font-mono text-xs">Click a block to edit it</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PREVIEW mode */}
          {mode === 'preview' && (
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] p-8">
              <div className="max-w-[600px] mx-auto">
                <div className="bg-[#2a2a2a] rounded-t px-4 py-2 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/60" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-gray-500 font-mono text-xs ml-2 truncate">{subject}</span>
                </div>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
              </div>
              <p className="text-center text-gray-700 font-mono text-[10px] mt-4">This is an approximation. Test in a real email client before sending.</p>
            </div>
          )}

          {/* CODE mode */}
          {mode === 'code' && (
            <div className="bg-[#0d0d0d] border border-[#1E1E1E]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E1E1E]">
                <div>
                  <p className="font-mono text-xs text-white">HTML Output — ready to paste</p>
                  <p className="font-mono text-[10px] text-gray-600 mt-0.5">Copy this into Mailchimp, MailerLite, Sendinblue, or any email platform's "Custom HTML" field.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={copyHtml}
                    className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-[#2a2a2a] text-gray-300 hover:text-white'}`}>
                    <Copy className="w-3 h-3" />{copied ? 'Copied!' : 'Copy All'}
                  </button>
                  <button onClick={downloadHtml}
                    className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs bg-[#2a2a2a] text-gray-300 hover:text-white transition-colors">
                    <Download className="w-3 h-3" />Download .html
                  </button>
                </div>
              </div>
              <pre className="p-4 text-xs text-green-400/70 font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-[70vh] overflow-y-auto">{html}</pre>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default EmailDesigner;
